import {Location, Photo, PredictEstimateState as EstimateState} from '@/model/api';
import {Estimate} from '@/model/Estimate';
import {HttpClient} from '@/model/http';
import {isLeft, map} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';

interface TemplateForNewEstimate {
    id: string;
    created: false;
    estateType: string;
}

interface ExistingEstimate {
    id: string;
    created: true;
}

export default class PredictEstimate implements Estimate<EstimateState> {
    private static readonly BASE_ULR = '/api/predict-estimate/estimates';
    private static readonly HEADERS = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    constructor(
        private estimate: TemplateForNewEstimate | ExistingEstimate,
        private readonly http: HttpClient,
    ) {
    }

    public async uploadPhoto(file: File, position: number): Promise<Photo> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('position', String(position));

        const response = await this.http.safeRequest<Photo>(
            this.estimateUrl(this.getExistingEstimateId()) + '/photos',
            {
                method: 'POST',
                body: formData,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    public async changePhotoDescription(photoId: string, description: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId + '/description',
            {
                method: 'PUT',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify({description}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async changePhotoPosition(photoId: string, position: number): Promise<void> {
        const response = await this.http.safeRequest(
            this.estimateUrl(this.getExistingEstimateId()) + '/photos/' + photoId + '/position',
            {
                method: 'PUT',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify({position}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async removePhoto(photoId: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId,
            {
                method: 'DELETE',
                headers: PredictEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async rotatePhoto(photoId: string, angle: number): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId + '/rotate',
            {
                method: 'POST',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify({angle}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async updateParameters(description: object): Promise<EstimateState> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimateId) + '/parameters',
            {
                method: 'PATCH',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify(description),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    public async selectLocation(location: Location): Promise<EstimateState> {
        const estimate = this.estimate;

        if (estimate.created) {
            return this.updateLocation(estimate.id, location);
        }

        return this.createNewEstimate(estimate, location);
    }

    public async createEstimate() {
        const response = await this.http.safeRequest<{ calculationUrl: string }>(
            this.estimateUrl(this.getExistingEstimateId()) + '/calculate',
            {
                method: 'POST',
                headers: PredictEstimate.HEADERS,
            },
        );

        return pipe(response, map(json => json.calculationUrl));
    }

    public async getEstimate(): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(this.estimateUrl(this.getExistingEstimateId()));

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    private async updateLocation(estimateId: string, location: Location): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimateId) + '/location',
            {
                method: 'PUT',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify({id: location.id}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    private async createNewEstimate(estimate: TemplateForNewEstimate, location: Location): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimate.id),
            {
                method: 'PUT',
                headers: PredictEstimate.HEADERS,
                body: JSON.stringify({
                    estateType: estimate.estateType,
                    location: location.id,
                }),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        this.estimate = {
            id: estimate.id,
            created: true,
        };

        return response.right;
    }

    private estimateUrl(estimateId: string): string {
        return `${PredictEstimate.BASE_ULR}/${estimateId}`;
    }

    private getExistingEstimateId(): string {
        const estimate = this.estimate;

        if (estimate.created) {
            return estimate.id;
        }

        throw new Error('Estimate ID not set, estimate wasn\'t created yet');
    }
}
